import { CloseOutlined, PlusOutlined, RedoOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import { blankVariable } from '@mm/state/helpers/connection-helpers';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import {
  ConnectionVariableKind,
  ConnectionVariableModel,
} from '@mm/state/model/ConnectionVariableModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AttributesMappingRow } from './AttributesMappingRow/AttributesMappingRow';
import styles from './IntegrationMappingModal.module.css';
import { CustomAttributeModel } from './types';

export const IntegrationMappingModal = ({
  connection,
}: {
  connection: ConnectionModel;
}) => {
  const { closeModal } = useModalActions();
  const { update } = useConnectionCRUDActions();
  const { getAttributes } = useSalesForceCRUDActions();
  const salesForceAttributes = useSelector(
    (state: StoreState) => state.salesForce.attributes,
  );
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const [connectionVariables, setConnectionVariables] = useState<
    ConnectionVariableModel[]
  >(
    connection.variables?.length
      ? connection.variables
      : [blankVariable(ConnectionVariableKind.toSend)],
  );

  const salesForceAttributeOptions = salesForceAttributes.map(attr => ({
    id: attr.name,
    name: attr.label,
  }));

  const updateConnection = async () => {
    if (!connection) {
      return;
    }
    closeModal();
    await update(activeFbPageId, {
      ...connection,
      variables: connectionVariables.filter(v => v.name && v.sentAs),
    });
  };

  const addVariable = () => {
    const updatedVariables = [
      ...connectionVariables,
      blankVariable(ConnectionVariableKind.toSend),
    ];
    setConnectionVariables(updatedVariables);
  };

  const filterOptions = (variable: ConnectionVariableModel) =>
    salesForceAttributeOptions.filter(
      attr =>
        !connectionVariables.some(
          v => v.id !== variable.id && v.sentAs === attr.id,
        ),
    );

  const onChangeMapping = (
    index: number,
    variable: ConnectionVariableModel,
    updatedField: Partial<ConnectionVariableModel>,
  ) => {
    setConnectionVariables(state => [
      ...state.slice(0, index),
      { ...variable, ...updatedField },
      ...state.slice(index + 1),
    ]);
  };

  const onRefreshClick = () => {
    if (!connection.salesforceAccessToken?.id) return;
    getAttributes(activeFbPageId, connection.salesforceAccessToken.id, true);
  };

  const onDeleteMappingRow = (index: number) => {
    setConnectionVariables(state => state.filter((_, idx) => idx !== index));
  };

  const [customValues, setCustomValues] = useState<CustomAttributeModel[]>([]);

  useEffect(() => {
    if (connection && connection.variables && connection.variables.length > 0) {
      const values: CustomAttributeModel[] = [];
      connection.variables.forEach(e => {
        if (e.value && e.static) {
          values.push({ id: e.value, value: e.value, name: e.value });
        }
      });
      setCustomValues(values);
    }
  }, [connection]);

  useEffect(() => {
    if (connection.salesforceAccessToken?.id) {
      getAttributes(activeFbPageId, connection.salesforceAccessToken.id);
    }
  }, []);

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />

      <div className={styles.title}>Choose Attributes to Sync</div>
      <div className={styles.content}>
        <div className={styles.mapping}>
          Please assign an attribute to each of the fields:
        </div>
        <div className={styles.mapper_container}>
          <div className={styles.mapperHeader}>
            <div
              className={classNames(
                styles.mapperHeaderTitle,
                styles.mapperHeaderWithButton,
              )}
            >
              Attribute
              <RedoOutlined onClick={onRefreshClick} />
            </div>
            <div className={styles.mapperHeaderTitle}>Field to Map To</div>
          </div>
          <div
            className={classNames(styles.mapperBody, {
              [styles.padding]: connectionVariables.length > 7,
            })}
          >
            {connectionVariables?.map((variable, idx) => (
              <AttributesMappingRow
                key={uniqueId(variable.name)}
                variable={variable}
                connectionVariables={connectionVariables}
                outerOptions={filterOptions(variable)}
                // customOuterOptions={customOuterOptions}
                // setCustomOuterOptions={setCustomOuterOptions}
                customValues={customValues}
                setCustomValues={setCustomValues}
                onChange={updatedField =>
                  onChangeMapping(idx, variable, updatedField)
                }
                onDelete={() => onDeleteMappingRow(idx)}
              />
            ))}
          </div>
        </div>
        <Button
          color="secondary-blue"
          css={{ container: styles.secondaryButton }}
          onClick={addVariable}
        >
          <PlusOutlined />
          Add
        </Button>
      </div>
      <div className={styles.buttons}>
        <Button
          color="transparent-no-shadow"
          css={{ container: styles.cancelButton }}
          onClick={() => closeModal()}
        >
          Cancel
        </Button>
        <Button css={{ container: styles.button }} onClick={updateConnection}>
          Save
        </Button>
      </div>
    </div>
  );
};
